import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  RadioGroup,
  Textarea,
  RadioGroupItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Calendar,
} from "@b2b/ui-components";
import { CalendarIcon } from "lucide-react";

import { format } from "date-fns";
import { cn } from "../lib/utils";
import { ReusableUploader } from "../components/ReusableUploader";
export type DynamicFieldOption = string | { label: string; value: string };

type FieldType =
  | "input"
  | "text"
  | "password"
  | "email"
  | "number"
  | "textarea"
  | "select"
  | "date"
  | "radio"
  | "file";

export type FieldConfigLike = {
  name: string;
  label: string;
  type: FieldType | string;
  inputType?: string;
  placeholder?: string;
  options?: DynamicFieldOption[];
  multiple?: boolean;
  accept?: string | Record<string, string[] | undefined>;
  maxSizeMB?: number;
};

type DynamicFieldProps = {
  field: FieldConfigLike;
  value: any;
  onChange: (value: any) => void;
  filesMap?: Record<string, File[]>;
  setFilesMap?: React.Dispatch<React.SetStateAction<Record<string, File[]>>>;
};

export const DynamicField = ({
  field,
  value,
  onChange,
  filesMap,
  setFilesMap,
}: DynamicFieldProps) => {
  const fieldId = `field-${field.name}`;

  switch (field.type) {
    case "input":
    case "text":
    case "password":
    case "email":
    case "number":
      return (
        <div className="space-y-2 col-span-1">
          <Label htmlFor={fieldId}>{field.label}</Label>
          <Input
            id={fieldId}
            type={field.inputType || field.type || "text"}
            placeholder={field.placeholder || `Enter ${field.label}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );

    case "textarea":
      return (
        <div className="space-y-2 col-span-1 sm:col-span-2 lg:col-span-3 w-full">
          <Label htmlFor={fieldId}>{field.label}</Label>
          <Textarea
            id={fieldId}
            placeholder={field.placeholder || `Enter ${field.label}`}
            className="min-h-[120px]"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );

    case "select":
      return (
        <div className="space-y-2 col-span-1">
          <Label htmlFor={fieldId}>{field.label}</Label>
          <Select value={value || ""} onValueChange={(val) => onChange(val)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={`Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent className="w-[var(--radix-select-trigger-width)] bg-white">
              {field.options?.map((opt) => {
                const optionValue = typeof opt === "string" ? opt : opt.value;
                const optionLabel = typeof opt === "string" ? opt : opt.label;
                return (
                  <SelectItem
                    key={optionValue}
                    value={optionValue}
                    className="bg-white hover:bg-gray-200 outline-none"
                  >
                    {optionLabel}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      );

    case "date":
      return (
        <div className="space-y-2 col-span-1">
          <Label htmlFor={fieldId}>{field.label}</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !value && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {value ? format(value, "PPP") : `Pick ${field.label}`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white" align="start">
              <Calendar
                mode="single"
                selected={value}
                onSelect={(date) => onChange(date)}
                className="p-3 w-[250px]"
              />
            </PopoverContent>
          </Popover>
        </div>
      );

    case "radio":
      return (
        <div className="space-y-4 col-span-1 sm:col-span-2 lg:col-span-3">
          <Label>{field.label}</Label>
          <RadioGroup
            value={value || ""}
            onValueChange={(val) => onChange(val)}
            className="flex flex-wrap gap-4"
          >
            {field.options?.map((opt) => {
              const optionValue = typeof opt === "string" ? opt : opt.value;
              const optionLabel = typeof opt === "string" ? opt : opt.label;
              const radioId = `${field.name}-${optionValue}`;
              return (
                <div key={optionValue} className="flex items-center space-x-2">
                  <RadioGroupItem value={optionValue} id={radioId} />
                  <Label htmlFor={radioId}>{optionLabel}</Label>
                </div>
              );
            })}
          </RadioGroup>
        </div>
      );

    case "file": {
      // ✅ normalize accept
      let acceptProp: Record<string, string[]> | undefined;
      if (typeof field.accept === "string") {
        acceptProp = { [field.accept]: [] };
      } else if (field.accept) {
        // remove undefined values safely
        acceptProp = Object.fromEntries(
          Object.entries(field.accept).filter(([_, v]) => Array.isArray(v))
        ) as Record<string, string[]>;
      }

      return (
        <div className="flex-1 min-w-[250px]">
          <ReusableUploader
            fieldKey={field.name}
            label={field.label}
            multiple={field.multiple}
            accept={acceptProp}
            maxSizeMB={field.maxSizeMB || 5}
            filesMap={filesMap || {}}
            setFilesMap={setFilesMap!}
          />
        </div>
      );
    }

    default:
      return null;
  }
};
